import '../../assets/styles/CameraFont.module.css';

import { chargePreAuthorization, useStoreProducts } from '@gozoki/api';
import { PreAuthorization, PreAuthorizationStatus, Product } from '@gozoki/api-types';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { Button } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { ToastProps } from '../../components/communication/Toast';
import TheftChargeModal from './TheftChargeModal';

const STATUS_COLORS: Record<PreAuthorizationStatus, string> = {
    NONE: '#2E7D32',
    PENDING: '#ED6C02',
    SUCCESS: '#1976D2',
    FAILED: '#D32F2F',
};

const getStatusLabel = (preAuthorization: PreAuthorization): string | null => {
    switch (preAuthorization.status) {
        case 'PENDING':
            return 'Débit en cours...';
        case 'SUCCESS':
            return `Débité: ${preAuthorization.chargeAmount?.toFixed(2) ?? '?'}€`;
        case 'FAILED':
            return `Échec: ${preAuthorization.chargeError ?? 'Erreur inconnue'}`;
        default:
            return null;
    }
};

interface PreAuthorizationCardProps {
    preAuthorization: PreAuthorization;
    products: Product[];
    storeId: number;
    showToast: (props: ToastProps) => void;
}

const PreAuthorizationCard = ({
    preAuthorization,
    products,
    storeId,
    showToast,
}: PreAuthorizationCardProps) => {
    const [modalOpen, setModalOpen] = useState(false);
    const timeString = new Date(preAuthorization.cpiCreatedAt).toLocaleTimeString();
    const color = STATUS_COLORS[preAuthorization.status ?? 'NONE'];
    const statusLabel = getStatusLabel(preAuthorization);
    const isButtonDisabled = preAuthorization.status === 'PENDING' || preAuthorization.status === 'SUCCESS';

    const { mutateAsync, isPending } = useMutation({
        mutationFn: (amount: number) =>
            chargePreAuthorization({
                storeId,
                preAuthorizationId: preAuthorization.id,
                amount,
            }),
        onError: () => {
            showToast({
                severity: 'error',
                message: 'Erreur lors du débit de la pré-autorisation',
            });
        },
    });

    const handleConfirm = async (amountInCents: number) => {
        await mutateAsync(amountInCents);
        showToast({
            severity: 'success',
            message: 'Demande de débit envoyée',
        });
        setModalOpen(false);
    };

    return (
        <div style={styles.card}>
            <div style={styles.content}>
                <div style={{ ...styles.icon, backgroundColor: `${color}1A`, color }}>
                    <CreditCardIcon style={{ width: '16px', height: '16px' }} />
                </div>
                <div style={styles.description}>
                    <h4 style={{ ...styles.title, color }}>
                        {preAuthorization.id} - {preAuthorization.cpiId}
                    </h4>
                    <p style={styles.paragraph}>
                        {timeString}
                        {statusLabel && (
                            <span style={{ marginLeft: '8px', color, fontWeight: 600 }}>
                                — {statusLabel}
                            </span>
                        )}
                    </p>
                </div>
            </div>
            <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => setModalOpen(true)}
                disabled={isButtonDisabled}
                style={{ marginRight: '8px' }}
            >
                Débiter
            </Button>
            <TheftChargeModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onConfirm={handleConfirm}
                products={products}
                loading={isPending}
            />
        </div>
    );
};

interface PreAuthorizationCardsProps {
    preAuthorizations: PreAuthorization[];
    storeId: number;
    showToast: (props: ToastProps) => void;
}

const PreAuthorizationCards = ({
    preAuthorizations,
    storeId,
    showToast,
}: PreAuthorizationCardsProps) => {
    const { data: products } = useStoreProducts(storeId);

    return (
        <>
            <div
                style={{
                    fontFamily: 'BlockBerthold_regular',
                    fontSize: '32px',
                    fontWeight: 400,
                    lineHeight: '40px',
                    textAlign: 'left',
                    textUnderlinePosition: 'from-font',
                    textDecorationSkipInk: 'none',
                }}
            >
                PRE-AUTORISATIONS
            </div>

            <div style={styles.listContainer}>
                {preAuthorizations.length === 0 && (
                    <p style={{ fontFamily: 'Avenir Next', color: '#888' }}>
                        Aucune pré-autorisation en cours
                    </p>
                )}
                {preAuthorizations
                    .slice()
                    .sort(
                        (a, b) =>
                            new Date(b.cpiCreatedAt).getTime() - new Date(a.cpiCreatedAt).getTime()
                    )
                    .map((preAuth) => (
                        <PreAuthorizationCard
                            key={preAuth.id}
                            preAuthorization={preAuth}
                            products={products ?? []}
                            storeId={storeId}
                            showToast={showToast}
                        />
                    ))}
            </div>
        </>
    );
};

const styles = {
    card: {
        fontFamily: 'Avenir Next',
        width: '100%',
        height: '64px',
        borderRadius: '6px',
        border: '1px solid #E2E8F0',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
    },
    description: {
        marginLeft: '10px',
        textIndent: '0',
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: '4px',
    },
    title: {
        margin: 0,
    },
    paragraph: {
        margin: 0,
    },
    content: {
        display: 'flex',
        alignItems: 'center',
        flexGrow: 1,
    },
    icon: {
        fontSize: '32px',
        marginRight: '8px',
        width: '48px',
        height: '48px',
        borderRadius: '4px',
        marginLeft: '8px',
        alignItems: 'center',
        justifyContent: 'center',
        display: 'flex',
    },
    listContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'flex-start',
        gap: '8px',
    },
};

export default PreAuthorizationCards;
